import { Checkbox, Radio, RadioGroup, Box } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { PermissionGuard } from '~/core/PermissionGuard';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

const InvoiceMechanicsSelectorRow = ({
  value,
  label,
  isLastRow,
  isDisabled = false,
}: {
  value: string;
  label: string;
  isLastRow: boolean;
  isDisabled?: boolean;
}) => {
  return (
    <Box
      borderBottomWidth={isLastRow ? undefined : '1px'}
      borderColor={colors.gray[100]}
      p="16px 12px"
    >
      <Radio value={value} isDisabled={isDisabled}>
        <Typography variant="base-muted">{label}</Typography>
      </Radio>
    </Box>
  );
};

export const InvoiceMechanicsSelectorTable = ({
  invoiceMechanicsOptions,
  invoiceMechanicsOption,
  setInvoiceMechanicsOption,
  selectedPricingOption,
  isDisabled,
}: {
  invoiceMechanicsOptions: { label: string; value: string }[];
  invoiceMechanicsOption: string | undefined;
  setInvoiceMechanicsOption: (option: string) => void;
  selectedPricingOption: string;
  isDisabled?: boolean;
}) => {
  const { context } = useFundingRequestTemplateContextV1();
  const { setValue } = context;

  return (
    <RadioGroup
      onChange={(newInvoiceMechanicsOptionKey) => {
        setInvoiceMechanicsOption(newInvoiceMechanicsOptionKey);
        if (newInvoiceMechanicsOptionKey) {
          setValue({
            type: 'setPricingOptions',
            options: {
              pricingOptionKey: selectedPricingOption, // keep the same pricing option
              drawRequestKey: undefined,
              invoiceMechanicsKey: newInvoiceMechanicsOptionKey, // update selected invoice mechanics key
            },
          });
        }
      }}
      value={invoiceMechanicsOption}
      isDisabled={context.allowedActions === 'readOnly' || isDisabled}
    >
      <Box
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="8px"
        backgroundColor="white"
        mt="16px"
      >
        {invoiceMechanicsOptions.map((option, index) => {
          return (
            <InvoiceMechanicsSelectorRow
              key={option.value}
              value={option.value}
              label={option.label}
              isLastRow={index === invoiceMechanicsOptions.length - 1}
              isDisabled={isDisabled}
            />
          );
        })}
      </Box>
    </RadioGroup>
  );
};

export const InvoiceMechanicsSelect = ({
  selectedPricingOption,
  isDisabled,
}: {
  selectedPricingOption: string;
  isDisabled?: boolean;
}) => {
  const { context } = useFundingRequestTemplateContextV1();
  const {
    data: { pricingOptions },
    setValue,
  } = context;

  const [useInvoiceMechanics, setUseInvoiceMechanics] = useState(false);
  const [invoiceMechanicsOption, setInvoiceMechanicsOption] = useState<
    string | undefined
  >(undefined);

  const invoiceMechanicsOptions = useMemo(() => {
    const selectedPricingOptionMechanics = pricingOptions.find(
      (option) => option.key === selectedPricingOption,
    );

    if (!selectedPricingOptionMechanics) {
      return null;
    }

    const singleInvoiceMechanicsOptions = selectedPricingOptionMechanics.invoiceMechanics.single.map(
      (mechanic) => ({
        label: mechanic.name,
        value: mechanic.key,
      }),
    );

    const recurringInvoiceMechanicsOptions = selectedPricingOptionMechanics.invoiceMechanics.recurring.map(
      (mechanic) => ({
        label: mechanic.name,
        value: mechanic.key,
      }),
    );

    return [
      ...singleInvoiceMechanicsOptions,
      ...recurringInvoiceMechanicsOptions,
    ];
  }, [selectedPricingOption, pricingOptions]);

  useEffect(() => {
    if (context.allowedActions === 'readOnly') {
      // @dani update readonly mode to show the actual selected invoice mechanics option after @jendevelops updates the pricing option to use the real one in the funding request template context
      return;
    }

    if (invoiceMechanicsOptions?.length) {
      // if there is at least one invoice mechanics option for the selected pricing option, default to use invoice mechanics and show the invoice mechanics selection field
      setUseInvoiceMechanics(true);

      // automatically select the first option and update the pricing option in the funding request template context
      setInvoiceMechanicsOption(invoiceMechanicsOptions[0].value);
      setValue({
        type: 'setPricingOptions',
        options: {
          pricingOptionKey: selectedPricingOption, // keep the same pricing option
          drawRequestKey: undefined,
          invoiceMechanicsKey: invoiceMechanicsOptions[0].value, // update selected invoice mechanics key
        },
      });
    }
  }, [
    invoiceMechanicsOptions,
    selectedPricingOption,
    setValue,
    context.allowedActions,
  ]);

  // if invoice mechanics are not configured, do not show invoice mechanics selection fields
  if (!invoiceMechanicsOptions?.length) {
    return null;
  }

  return (
    <PermissionGuard
      permissions={(permissionsContext) =>
        permissionsContext.flags.productsInvoiceMechanicsPermissions
      }
      fallback={null} // simply do not display component if permissions are denied
    >
      <Checkbox
        mt="16px"
        isChecked={useInvoiceMechanics}
        onChange={(e) => {
          if (context.allowedActions === 'readOnly') {
            return;
          }

          setUseInvoiceMechanics(Boolean(e.target.checked));

          if (e.target.checked) {
            // if user manually checks the checkbox automatically select the first invoice mechanics option
            setInvoiceMechanicsOption(invoiceMechanicsOptions[0].value);
            setValue({
              type: 'setPricingOptions',
              options: {
                pricingOptionKey: selectedPricingOption, // keep the same pricing option
                drawRequestKey: undefined,
                invoiceMechanicsKey: invoiceMechanicsOptions[0].value, // update selected invoice mechanics key
              },
            });
          } else {
            // if user manually unchecks the checkbox, clear the invoice mechanics option from the pricing option in the funding request template context
            setInvoiceMechanicsOption(undefined);
            setValue({
              type: 'setPricingOptions',
              options: {
                pricingOptionKey: selectedPricingOption, // keep the same pricing option
                drawRequestKey: undefined,
                invoiceMechanicsKey: undefined, // clear invoice mechanics key
              },
            });
          }
        }}
        isDisabled={context.allowedActions === 'readOnly' || isDisabled}
      >
        <Typography variant="base-muted">Preset Invoice</Typography>
      </Checkbox>
      {useInvoiceMechanics && (
        <InvoiceMechanicsSelectorTable
          invoiceMechanicsOptions={invoiceMechanicsOptions}
          invoiceMechanicsOption={invoiceMechanicsOption}
          setInvoiceMechanicsOption={setInvoiceMechanicsOption}
          selectedPricingOption={selectedPricingOption}
        />
      )}
    </PermissionGuard>
  );
};
